import React from 'react'
import { RecurrenceFrequency, RecurringPatternValue } from '@/types/meeting'

interface RecurringPatternPickerProps {
  value: RecurringPatternValue
  onChange: (value: RecurringPatternValue) => void
  error?: string
}

const FREQUENCIES: { value: RecurrenceFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' }
]

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const RecurringPatternPicker: React.FC<RecurringPatternPickerProps> = ({ value, onChange, error }) => {
  const update = (patch: Partial<RecurringPatternValue>) => onChange({ ...value, ...patch })

  const toggleDay = (day: number) => {
    const daysOfWeek = value.daysOfWeek.includes(day)
      ? value.daysOfWeek.filter(d => d !== day)
      : [...value.daysOfWeek, day].sort()
    update({ daysOfWeek })
  }

  return (
    <div className="border-t border-gray-200 mt-4 pt-4">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <input
          type="checkbox"
          checked={value.enabled}
          onChange={e => update({ enabled: e.target.checked })}
        />
        Make this a recurring meeting
      </label>

      {value.enabled && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
              <select
                value={value.frequency}
                onChange={e => update({ frequency: e.target.value as RecurrenceFrequency })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {FREQUENCIES.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Every</label>
              <input
                type="number"
                min={1}
                value={value.interval}
                onChange={e => update({ interval: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {(value.frequency === 'weekly' || value.frequency === 'biweekly') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Days of week (optional)</label>
              <div className="flex gap-2 flex-wrap">
                {DAY_LABELS.map((label, day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`px-2 py-1 text-xs rounded border ${
                      value.daysOfWeek.includes(day)
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ends</label>
            <div className="flex items-center gap-4 mb-2">
              <label className="flex items-center gap-1 text-sm text-gray-700">
                <input
                  type="radio"
                  name="recurring-end-mode"
                  checked={value.endMode === 'occurrences'}
                  onChange={() => update({ endMode: 'occurrences' })}
                />
                After a number of occurrences
              </label>
              <label className="flex items-center gap-1 text-sm text-gray-700">
                <input
                  type="radio"
                  name="recurring-end-mode"
                  checked={value.endMode === 'date'}
                  onChange={() => update({ endMode: 'date' })}
                />
                On a date
              </label>
            </div>
            {value.endMode === 'occurrences' ? (
              <input
                type="number"
                min={1}
                aria-label="Occurrences count"
                value={value.maxOccurrences}
                onChange={e => update({ maxOccurrences: parseInt(e.target.value) || '' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            ) : (
              <input
                type="date"
                aria-label="End date"
                value={value.endDate}
                onChange={e => update({ endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            )}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  )
}

export default RecurringPatternPicker
